// React Query hooks for the One-on-One Coach (Aria).

import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '../queryKeys'
import {
  appendCoachVoiceTurns,
  deleteUserGoal,
  endCoachConversation,
  getCoachConversation,
  getCoachConversations,
  getCoachStatus,
  getUserGoals,
  requestGoalSetting,
  saveUserGoals,
  sendCoachMessage,
  startCoachConversation,
  updateUserGoal
} from '../processHandlers/coach'
import type {
  AppendCoachVoiceTurnsRequest,
  DeleteUserGoalRequest,
  EndCoachConversationRequest,
  RequestGoalSettingRequest,
  SaveUserGoalsRequest,
  SendCoachMessageRequest,
  StartCoachConversationRequest,
  UpdateUserGoalRequest
} from '../../../../pure-js/types/CoachApi'
import type { UserGoal } from '../../../../pure-js/types/CoachTypes'

// Coach state changes as a direct result of user actions -> keep it fresh.
export const useCoachStatus = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.COACH_STATUS],
    queryFn: () => getCoachStatus().then((response) => response.data),
    staleTime: 0,
    enabled
  })

export const useCoachConversations = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.COACH_CONVERSATIONS],
    queryFn: () => getCoachConversations().then((response) => response.data),
    staleTime: 0,
    enabled
  })

// One thread and its messages: what the page shows, and what a send that lost its answer reads again.
export const coachConversationQuery = (conversationId: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.COACH_CONVERSATION_DETAIL(conversationId),
    queryFn: () => getCoachConversation({ conversationId }).then((response) => response.data),
    staleTime: 0
  })

export const useCoachConversation = (conversationId: string | undefined) =>
  useQuery({ ...coachConversationQuery(conversationId || ''), enabled: Boolean(conversationId) })

export const useUserGoals = (userId?: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.USER_GOALS, userId ?? 'me'],
    queryFn: () => getUserGoals({ userId }).then((response) => response.data.goals),
    staleTime: 0
  })

export const useStartCoachConversation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: StartCoachConversationRequest) => startCoachConversation(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEYS.COACH_CONVERSATIONS] })
  })
}

/** The goals panel and the onboarding state, after a typed turn that may have saved or retired a goal. */
export const refreshGoalsAfterTurn = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_GOALS] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COACH_STATUS] })
}

// A typed turn can settle the user's goals, and the backend saves them on that turn rather than waiting
// for the conversation to be concluded. It can also RETIRE one they say they are done with, which writes
// no new goal — keying the refresh on `goals` alone left the panel showing what was just retired.
export const useSendCoachMessage = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: SendCoachMessageRequest) => sendCoachMessage(req),
    onSuccess: (response) => {
      if (!response.data.goals?.length && !response.data.completedGoals?.length) return
      refreshGoalsAfterTurn(qc)
    }
  })
}

// Only the list is refreshed here, so the message count moves while the user is still talking. The
// thread itself is refetched when the spoken session pauses — refetching it after every turn would
// double the traffic of a live conversation, for a view nobody is looking at yet.
export const useAppendCoachVoiceTurns = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: AppendCoachVoiceTurnsRequest) => appendCoachVoiceTurns(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEYS.COACH_CONVERSATIONS] })
  })
}

export const useEndCoachConversation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: EndCoachConversationRequest) => endCoachConversation(req),
    onSuccess: (_response, request) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEYS.COACH_CONVERSATIONS] })
      qc.invalidateQueries({ queryKey: [QUERY_KEYS.COACH_STATUS] })
      // Onboarding goals are extracted when a conversation ends, so the goals panel is stale until now.
      qc.invalidateQueries({ queryKey: [QUERY_KEYS.USER_GOALS] })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.COACH_CONVERSATION_DETAIL(request.conversationId) })
    }
  })
}

export const useSaveUserGoals = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: SaveUserGoalsRequest) => saveUserGoals(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEYS.USER_GOALS] })
      qc.invalidateQueries({ queryKey: [QUERY_KEYS.COACH_STATUS] })
    }
  })
}

// Both answer with the caller's whole list, written straight into the cache: invalidating instead costs
// a round trip during which the row still renders the old wording.
const applyOwnGoals = (queryClient: ReturnType<typeof useQueryClient>, goals: UserGoal[]) =>
  queryClient.setQueryData([QUERY_KEYS.USER_GOALS, 'me'], goals)

export const useUpdateUserGoal = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: UpdateUserGoalRequest) => updateUserGoal(req),
    onSuccess: (response) => applyOwnGoals(queryClient, response.data.goals)
  })
}

export const useDeleteUserGoal = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: DeleteUserGoalRequest) => deleteUserGoal(req),
    onSuccess: (response) => applyOwnGoals(queryClient, response.data.goals)
  })
}

// Facilitator/admin re-opens goal-setting for a participant (from the org members view).
export const useRequestGoalSetting = () =>
  useMutation({ mutationFn: (req: RequestGoalSettingRequest) => requestGoalSetting(req) })
